import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Sparkles, Clock, Play, Users, User, ArrowRight, Wand2 } from "lucide-react";

const MODE_LABELS = { self_paced: "Self-paced", live_group: "Live group", one_to_one: "One-to-one" };
const MODE_ICONS = { self_paced: Play, live_group: Users, one_to_one: User };

const BAND_HINT = {
  high:     "Your recent screening suggests you could use extra support right now.",
  moderate: "A little care goes a long way — try one of these today.",
  mild:     "Gentle practices your latest screening suggests will help.",
  normal:   "Keep the balance going with these on-brand picks.",
};

/** Home widget: one FEATURED therapy card + up to three other recommendations,
 * based on the patient's latest voice-biomarker band. */
export function FeaturedTherapyCard() {
  const [data, setData] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/datp/recommendations?limit=4").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data || !data.picks?.length) return null;
  const [featured, ...rest] = data.picks;
  const Icon = MODE_ICONS[featured.mode] || Play;

  return (
    <section
      className="rounded-3xl bg-gradient-to-br from-[color:var(--mano-primary)] to-[color:var(--mano-primary-hover)] text-white p-6 lg:p-7 relative overflow-hidden"
      data-testid="home-featured-therapy"
    >
      <div className="absolute inset-0 opacity-10 pointer-events-none"
           style={{ backgroundImage: "radial-gradient(circle at 20% 10%, #fff 1px, transparent 1px)",
                    backgroundSize: "22px 22px" }} />
      <div className="relative flex flex-col lg:flex-row gap-6 items-start">
        <div className="flex-1 min-w-0">
          <span className="inline-flex items-center gap-1.5 uppercase-label text-white/80">
            <Wand2 size={13} /> Recommended for you
          </span>
          <div className="mt-2 text-white/85 text-sm">
            {BAND_HINT[data.band] || BAND_HINT.normal}
          </div>
          <button
            onClick={() => nav("/therapies")}
            data-testid="featured-open"
            className="text-left mt-4 group w-full"
          >
            <div className="flex items-center gap-3">
              <div className="h-14 w-14 rounded-2xl bg-white/15 flex items-center justify-center shrink-0 group-hover:bg-white/25 transition-colors">
                <Icon size={22} />
              </div>
              <div className="min-w-0">
                <div className="uppercase-label text-white/70">
                  {featured.category_name}{featured.subcategory ? " · " + featured.subcategory : ""}
                </div>
                <div className="font-display text-2xl leading-tight mt-0.5 truncate">
                  {featured.title}
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs text-white/80">
                  <span className="inline-flex items-center gap-1 bg-white/15 px-2 py-0.5 rounded-full font-semibold">
                    {MODE_LABELS[featured.mode] || featured.mode}
                  </span>
                  {featured.duration_min ? (
                    <span className="inline-flex items-center gap-1"><Clock size={10} /> {featured.duration_min} min</span>
                  ) : null}
                </div>
              </div>
            </div>
          </button>
          {featured.description && (
            <p className="mt-4 text-sm text-white/80 leading-relaxed line-clamp-3">
              {featured.description}
            </p>
          )}
          <button
            onClick={() => nav("/therapies")}
            data-testid="featured-open-cta"
            className="mt-4 inline-flex items-center gap-1.5 bg-white text-[color:var(--mano-primary)] px-4 py-2 rounded-full text-sm font-semibold hover:bg-[color:var(--mano-mild)] transition-colors"
          >
            Open therapy library <ArrowRight size={14} />
          </button>
        </div>

        {rest.length > 0 && (
          <div className="w-full lg:w-64 shrink-0 space-y-2" data-testid="featured-side-picks">
            <div className="uppercase-label text-white/60">More like this</div>
            {rest.slice(0, 3).map((t) => {
              const RIcon = MODE_ICONS[t.mode] || Play;
              return (
                <button
                  key={t.id}
                  onClick={() => nav("/therapies")}
                  data-testid={`side-pick-${t.id}`}
                  className="w-full text-left flex items-center gap-3 bg-white/10 hover:bg-white/15 rounded-2xl p-3 transition-colors"
                >
                  <div className="h-9 w-9 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                    <RIcon size={14} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{t.title}</div>
                    <div className="text-[11px] text-white/70 truncate">
                      {t.category_name}{t.duration_min ? " · " + t.duration_min + "m" : ""}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
